import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
    MessageSquare, 
    Send, 
    Users, 
    Bell,
    BellOff,
    Search,
    Plus,
    Paperclip,
    Image as ImageIcon,
    FileText,
    MoreVertical,
    Trash2,
    Reply,
    Clock,
    AlertCircle,
    CheckCircle2,
    Megaphone,
    Lock,
    Hash,
    User,
    Calendar,
    Link as LinkIcon,
    X,
    Settings,
    AlertTriangle,
    Shield,
    Building2,
    ChefHat,
    Wine,
    UsersRound,
    Terminal,
    ArrowLeft
} from 'lucide-react';
import MobilePageHeader from '../components/MobilePageHeader';
import MobileConversationView from '../components/MobileConversationView';
import { cn } from '../lib/utils';

const MessagingPage = () => {
    const { api, user, hasPermission } = useAuth();
    const [loading, setLoading] = useState(true);
    const [groups, setGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const [unreadCount, setUnreadCount] = useState({ notifications: 0, messages: 0 });
    
    // Mobile state - detect if on mobile and show conversation fullscreen
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [mobileShowConversation, setMobileShowConversation] = useState(false);
    
    // Detect window resize
    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            // Reset mobile conversation view when switching to desktop
            if (!mobile) {
                setMobileShowConversation(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // IBAKA alert states
    const [showIbakaWarning, setShowIbakaWarning] = useState(false);
    const [ibakaConfirmed, setIbakaConfirmed] = useState(false);
    const [sendingIbaka, setSendingIbaka] = useState(false);
    
    // UI states
    const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [showRunningLateDialog, setShowRunningLateDialog] = useState(false);
    const [showNewPrivateDialog, setShowNewPrivateDialog] = useState(false);
    const [showAdminUsersDialog, setShowAdminUsersDialog] = useState(false);
    const [showWriteToAdminDialog, setShowWriteToAdminDialog] = useState(false); // Dialog pour écrire à l'admin
    const [adminView, setAdminView] = useState(false); // Admin mode toggle
    const [allConversations, setAllConversations] = useState([]); // For admin view
    const [adminUsers, setAdminUsers] = useState([]); // Users for admin direct messaging
    const [adminMessageData, setAdminMessageData] = useState({ subject: '', content: '' }); // Message to admin
    const [sendingAdminMessage, setSendingAdminMessage] = useState(false);
    
    // Form states
    const [deleteReason, setDeleteReason] = useState('');
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [runningLateData, setRunningLateData] = useState({ reason: '', eta_minutes: 15 });
    const [notificationSettings, setNotificationSettings] = useState(null);
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const previousUnreadRef = useRef(0);
    const notificationSoundRef = useRef(null);
    
    // Permissions
    const canManageGroups = hasPermission('manage_messaging');
    const canDeleteMessages = hasPermission('delete_messages');
    const isAdmin = user?.role === 'ADMIN';
    
    // Initialize notification sound
    useEffect(() => {
        // Load notification sound from public folder
        notificationSoundRef.current = new Audio('/notification.wav');
        notificationSoundRef.current.volume = 0.5;
    }, []);
    
    // Play notification sound
    const playNotificationSound = () => {
        if (notificationSoundRef.current && notificationSettings?.sound_enabled !== false) {
            notificationSoundRef.current.currentTime = 0;
            notificationSoundRef.current.play().catch(err => {
                console.log('Audio play blocked:', err);
            });
        }
    };
    
    // Play IBAKA emergency alert sound (distinct alarm)
    const playIbakaAlertSound = () => {
        try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            // Create a loud, attention-grabbing alarm pattern
            const playTone = (frequency, startTime, duration) => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();
                
                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);
                
                oscillator.frequency.value = frequency;
                oscillator.type = 'square';
                
                gainNode.gain.setValueAtTime(0.3, audioContext.currentTime + startTime);
                gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + startTime + duration);
                
                oscillator.start(audioContext.currentTime + startTime);
                oscillator.stop(audioContext.currentTime + startTime + duration);
            };
            
            // Play urgent alarm pattern: high-low-high-low (like emergency sirens)
            for (let i = 0; i < 4; i++) {
                playTone(880, i * 0.3, 0.15);       // High A
                playTone(440, i * 0.3 + 0.15, 0.15); // Low A
            }
            
            // Final long tone
            playTone(880, 1.2, 0.4);
            
        } catch (err) {
            console.log('IBAKA sound error:', err);
            // Fallback to regular notification sound
            playNotificationSound();
        }
    };
    
    // Check for new messages and notify
    const checkForNewMessages = async () => {
        try {
            const res = await api().get('/messaging/unread-count');
            const newUnread = res.data.unread_messages || 0;
            const hasIbakaAlert = res.data.has_ibaka_alert || false;
            
            // If unread count increased, show notification
            if (newUnread > previousUnreadRef.current && previousUnreadRef.current >= 0) {
                const diff = newUnread - previousUnreadRef.current;
                
                // Check if it's an IBAKA alert
                if (hasIbakaAlert) {
                    toast.error(
                        '🚨 ALERTE URGENCE IBAKA',
                        {
                            description: 'Une alerte d\'urgence a été déclenchée!',
                            duration: 15000,
                            icon: <AlertTriangle className="w-4 h-4 text-red-500" />,
                        }
                    );
                    
                    // Play IBAKA alarm sound
                    playIbakaAlertSound();
                    
                    // Show urgent browser notification
                    if (Notification.permission === 'granted') {
                        new Notification('🚨 ALERTE URGENCE IBAKA', {
                            body: 'Une alerte d\'urgence a été déclenchée! Vérifiez immédiatement.',
                            icon: '/logo192.png',
                            tag: 'ibaka-alert',
                            requireInteraction: true
                        });
                    }
                } else {
                    // Normal notification
                    toast.info(
                        `${diff} nouveau${diff > 1 ? 'x' : ''} message${diff > 1 ? 's' : ''}`,
                        {
                            description: 'Cliquez pour voir',
                            duration: 5000,
                            icon: <MessageSquare className="w-4 h-4" />,
                        }
                    );
                    
                    // Play normal sound
                    playNotificationSound();
                    
                    // Request browser notification if permitted
                    if (Notification.permission === 'granted') {
                        const notification = new Notification('ALDRIN\'S - Nouveau message', {
                            body: `Vous avez ${diff} nouveau${diff > 1 ? 'x' : ''} message${diff > 1 ? 's' : ''}`,
                            icon: '/logo192.png',
                            tag: 'new-message'
                        });
                        
                        // Navigate to messaging when notification is clicked
                        notification.onclick = () => {
                            window.focus();
                            window.location.href = '/messaging';
                            notification.close();
                        };
                    }
                }
            }
            
            previousUnreadRef.current = newUnread;
            setUnreadCount(res.data);
        } catch (error) {
            console.error('Error checking messages:', error);
        }
    };
    
    // Request notification permission and subscribe to push
    useEffect(() => {
        const setupPushNotifications = async () => {
            // Request permission
            if ('Notification' in window && Notification.permission === 'default') {
                await Notification.requestPermission();
            }
            
            // Subscribe to push if permission granted
            if ('Notification' in window && Notification.permission === 'granted' && 'serviceWorker' in navigator) {
                try {
                    const registration = await navigator.serviceWorker.ready;
                    
                    // Get VAPID public key from backend
                    const vapidRes = await api().get('/messaging/push/vapid-key');
                    const vapidPublicKey = vapidRes.data.publicKey;
                    
                    if (vapidPublicKey) {
                        // Convert VAPID key to Uint8Array
                        const urlBase64ToUint8Array = (base64String) => {
                            const padding = '='.repeat((4 - base64String.length % 4) % 4);
                            const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
                            const rawData = window.atob(base64);
                            const outputArray = new Uint8Array(rawData.length);
                            for (let i = 0; i < rawData.length; ++i) {
                                outputArray[i] = rawData.charCodeAt(i);
                            }
                            return outputArray;
                        };
                        
                        // Subscribe to push
                        const subscription = await registration.pushManager.subscribe({
                            userVisibleOnly: true,
                            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
                        });
                        
                        // Send subscription to backend
                        await api().post('/messaging/push/subscribe', subscription.toJSON());
                        console.log('Push subscription successful');
                    }
                } catch (err) {
                    console.log('Push subscription error:', err);
                }
            }
        };
        
        setupPushNotifications();
    }, []);
    
    // Fetch groups on mount
    useEffect(() => {
        fetchGroups();
        checkForNewMessages();
        fetchUsers();
        fetchNotificationSettings();
        
        // Initialize previous unread count
        previousUnreadRef.current = -1; // -1 means first load, don't notify
        
        // Poll for new messages every 5 seconds (faster for better UX)
        const interval = setInterval(() => {
            checkForNewMessages();
            if (selectedGroup) {
                fetchMessages(selectedGroup.id, true);
            }
        }, 5000);
        
        return () => clearInterval(interval);
    }, []);
    
    // Fetch messages when group changes
    useEffect(() => {
        if (selectedGroup) {
            fetchMessages(selectedGroup.id);
        }
    }, [selectedGroup?.id]);
    
    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);
    
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    const fetchGroups = async () => {
        try {
            const res = await api().get('/messaging/groups');
            setGroups(res.data);
            setLoading(false);
        } catch (error) {
            toast.error('Erreur lors du chargement des conversations');
            setLoading(false);
        }
    };
    
    const fetchMessages = async (groupId, silent = false) => {
        try {
            const res = await api().get(`/messaging/groups/${groupId}/messages`);
            setMessages(res.data);
            if (!silent) {
                fetchGroups(); // Refresh unread counts
            }
        } catch (error) {
            if (!silent) {
                toast.error('Erreur lors du chargement des messages');
            }
        }
    };
    
    const fetchUnreadCount = async () => {
        try {
            const res = await api().get('/messaging/unread-count');
            setUnreadCount(res.data);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };
    
    const fetchUsers = async () => {
        try {
            const res = await api().get('/messaging/users');
            setUsers(res.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };
    
    const fetchNotificationSettings = async () => {
        try {
            const res = await api().get('/messaging/settings');
            setNotificationSettings(res.data);
        } catch (error) {
            console.error('Error fetching notification settings:', error);
        }
    };
    
    // Admin functions
    const fetchAllConversations = async () => {
        if (!isAdmin) return;
        try {
            const res = await api().get('/messaging/admin/all-conversations');
            setAllConversations(res.data);
        } catch (error) {
            console.error('Error fetching all conversations:', error);
        }
    };
    
    const fetchAdminUsers = async () => {
        if (!isAdmin) return;
        try {
            const res = await api().get('/messaging/admin/users-for-messaging');
            setAdminUsers(res.data);
        } catch (error) {
            console.error('Error fetching admin users:', error);
        }
    };
    
    const fetchAdminMessages = async (groupId) => {
        try {
            const res = await api().get(`/messaging/admin/conversations/${groupId}/messages`);
            setMessages(res.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des messages');
        }
    };
    
    const sendAdminMessage = async (userId) => {
        if (!messageText.trim()) return;
        setSending(true);
        try {
            await api().post(`/messaging/admin/send-to-user/${userId}`, {
                content: messageText.trim()
            });
            toast.success('Message envoyé');
            setMessageText('');
            if (selectedGroup) {
                fetchAdminMessages(selectedGroup.id);
            }
            fetchAllConversations();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        } finally {
            setSending(false);
        }
    };
    
    // Envoyer un message confidentiel à l'administrateur
    const sendMessageToAdmin = async () => {
        if (!adminMessageData.content.trim()) {
            toast.error('Veuillez entrer un message');
            return;
        }
        setSendingAdminMessage(true);
        try {
            await api().post('/messaging/send-to-admin', {
                subject: adminMessageData.subject.trim() || null,
                content: adminMessageData.content.trim()
            });
            toast.success('Votre message a été envoyé à la direction');
            setAdminMessageData({ subject: '', content: '' });
            setShowWriteToAdminDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSendingAdminMessage(false);
        }
    };
    
    // Check if message triggers IBAKA
    const isIbakaTrigger = (text) => {
        if (!text) return false;
        const clean = text.trim().toUpperCase();
        return clean === 'IBAKA' || clean.startsWith('IBAKA ') || clean.startsWith('IBAKA\n');
    };
    
    const sendIbakaAlert = async () => {
        setSendingIbaka(true);
        try {
            // Get geolocation if available
            let latitude = null;
            let longitude = null;
            
            if (navigator.geolocation) {
                try {
                    const pos = await new Promise((resolve, reject) => {
                        navigator.geolocation.getCurrentPosition(resolve, reject, {
                            timeout: 5000,
                            enableHighAccuracy: true
                        });
                    });
                    latitude = pos.coords.latitude;
                    longitude = pos.coords.longitude;
                } catch (e) {
                    console.log('GPS not available');
                }
            }
            
            const isMobile = window.innerWidth < 1024;
            const res = await api().post('/messaging/ibaka', {
                message: messageText.trim(),
                device_type: isMobile ? 'mobile' : 'desktop',
                device_id: localStorage.getItem('device_id') || 'unknown',
                latitude,
                longitude
            });
            
            toast.success('🚨 Alerte IBAKA envoyée à tout le monde');
            
            // Play IBAKA alert sound
            playIbakaAlertSound();
            
            // Show first-time warning if applicable
            if (res.data.is_first_time && res.data.warning_message) {
                toast.warning(res.data.warning_message, { duration: 10000 });
            }
            
            setMessageText('');
            setShowIbakaWarning(false);
            setIbakaConfirmed(false);
            await fetchGroups();
            
        } catch (error) {
            const detail = error.response?.data?.detail || 'Erreur lors de l\'envoi';
            toast.error(detail);
        } finally {
            setSendingIbaka(false);
        }
    };
    
    const sendMessage = async () => {
        if (!messageText.trim() || !selectedGroup) return;
        
        // Check for IBAKA trigger
        if (isIbakaTrigger(messageText)) {
            setShowIbakaWarning(true);
            return;
        }
        
        setSending(true);
        try {
            await api().post('/messaging/messages', {
                group_id: selectedGroup.id,
                content: messageText.trim(),
                mentions: [],
                attachments: []
            });
            
            setMessageText('');
            await fetchMessages(selectedGroup.id);
            await fetchGroups();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
        } finally {
            setSending(false);
        }
    };
    
    const deleteMessage = async () => {
        if (!selectedMessage || !deleteReason.trim() || deleteReason.length < 5) {
            toast.error('Motif de suppression requis (min. 5 caractères)');
            return;
        }
        
        try {
            await api().delete(`/messaging/messages/${selectedMessage.id}`, {
                data: { reason: deleteReason }
            });
            
            toast.success('Message supprimé');
            setShowDeleteDialog(false);
            setDeleteReason('');
            setSelectedMessage(null);
            await fetchMessages(selectedGroup.id);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        }
    };
    
    const reportRunningLate = async () => {
        try {
            await api().post('/messaging/running-late', runningLateData);
            toast.success('Retard signalé à votre équipe');
            setShowRunningLateDialog(false);
            setRunningLateData({ reason: '', eta_minutes: 15 });
            await fetchGroups();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const startPrivateChat = async (otherUserId) => {
        try {
            const res = await api().post(`/messaging/private/${otherUserId}`);
            const conversation = res.data;
            
            // Refresh groups and select the new/existing conversation
            await fetchGroups();
            setSelectedGroup(conversation);
            setShowNewPrivateDialog(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };
    
    const updateNotificationSettings = async (newSettings) => {
        try {
            const res = await api().put('/messaging/settings', newSettings);
            setNotificationSettings(res.data);
            toast.success('Paramètres mis à jour');
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };
    
    const getGroupColor = (groupName) => {
        const colors = {
            'Direction': 'bg-purple-600',
            'Salle': 'bg-blue-500',
            'Cuisine': 'bg-orange-500',
            'Bar': 'bg-pink-500',
            'Tous': 'bg-emerald-500',
            'Annonces': 'bg-red-500',
            'SYSTÈME': 'bg-slate-600'
        };
        return colors[groupName] || 'bg-gray-500';
    };
    
    const getGroupIcon = (group) => {
        const icons = {
            'Direction': <Building2 className="w-5 h-5" />,
            'Salle': <Users className="w-5 h-5" />,
            'Cuisine': <ChefHat className="w-5 h-5" />,
            'Bar': <Wine className="w-5 h-5" />,
            'Tous': <UsersRound className="w-5 h-5" />,
            'Annonces': <Megaphone className="w-5 h-5" />,
            'SYSTÈME': <Terminal className="w-5 h-5" />
        };
        
        if (group.type === 'PRIVATE') {
            return <Lock className="w-5 h-5" />;
        }
        
        return icons[group.name] || <Hash className="w-5 h-5" />;
    };
    
    const formatMessageTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const isToday = date.toDateString() === now.toDateString();
        
        if (isToday) {
            return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        }
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) + ' ' +
               date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };
    
    const getInitials = (name) => {
        return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
    };
    
    const filteredGroups = groups.filter(g => 
        g.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (loading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
        );
    }
    
    // Mobile: Show conversation fullscreen when selected
    if (isMobile && mobileShowConversation && selectedGroup) {
        return (
            <MobileConversationView
                conversation={selectedGroup}
                messages={messages}
                currentUserId={user?.id}
                onBack={() => {
                    setMobileShowConversation(false);
                    setSelectedGroup(null);
                }}
                onSendMessage={async (text) => {
                    setSending(true);
                    try {
                        await api().post(`/messaging/groups/${selectedGroup.id}/messages`, {
                            content: text,
                            message_type: 'text'
                        });
                        fetchMessages(selectedGroup.id);
                    } catch (error) {
                        toast.error('Erreur envoi');
                    } finally {
                        setSending(false);
                    }
                }}
                sending={sending}
            />
        );
    }
    
    // Helper function to render conversation list item
    const renderConversationItem = (g, isAdminView = false) => {
        const isSelected = selectedGroup?.id === g.id;
        const hasUnread = (g.unread_count || 0) > 0;
        
        return (
            <button
                key={g.id}
                className={cn(
                    "w-full text-left p-3 rounded-lg transition-colors",
                    "hover:bg-muted/80 active:bg-muted",
                    isSelected && !isMobile && "bg-primary/10 border-l-2 border-primary",
                    hasUnread && "bg-primary/5"
                )}
                onClick={() => {
                    setSelectedGroup(g);
                    fetchMessages(g.id);
                    if (isMobile) {
                        setMobileShowConversation(true);
                    }
                }}
                data-testid={`conversation-${g.id}`}
            >
                <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className={cn(
                            "text-sm",
                            g.is_private ? "bg-blue-100 text-blue-600" : "bg-primary/10 text-primary"
                        )}>
                            {g.is_private ? (
                                <Lock className="w-4 h-4" />
                            ) : g.name?.includes('Cuisine') ? (
                                <ChefHat className="w-4 h-4" />
                            ) : g.name?.includes('Bar') ? (
                                <Wine className="w-4 h-4" />
                            ) : g.name?.includes('Direction') ? (
                                <Building2 className="w-4 h-4" />
                            ) : (
                                <Hash className="w-4 h-4" />
                            )}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <span className={cn(
                                "font-medium text-sm truncate",
                                hasUnread && "font-semibold"
                            )}>
                                {g.name || g.member_names?.filter(n => n !== user?.name).join(', ') || 'Conversation'}
                            </span>
                            {hasUnread && (
                                <Badge variant="default" className="h-5 min-w-5 px-1.5 text-xs shrink-0">
                                    {g.unread_count}
                                </Badge>
                            )}
                        </div>
                        {g.last_message && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {g.last_message.content?.substring(0, 40)}
                                {g.last_message.content?.length > 40 ? '...' : ''}
                            </p>
                        )}
                    </div>
                </div>
            </button>
        );
    };
    
    return (
        <div className="h-[calc(100vh-120px)] md:h-[calc(100vh-120px)] flex flex-col" data-testid="messaging-page">
            {/* Mobile Page Header - Only on mobile when NOT showing conversation */}
            <div className="md:hidden">
                <MobilePageHeader 
                    title="Messagerie"
                    subtitle={unreadCount.messages > 0 ? `${unreadCount.messages} non lu(s)` : undefined}
                    icon={MessageSquare}
                    backPath="/dashboard"
                    rightContent={
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowSettingsDialog(true)}
                            className="h-10 w-10"
                        >
                            <Settings className="w-5 h-5" />
                        </Button>
                    }
                />
            </div>
            
            {/* Desktop Header - Hidden on mobile */}
            <div className="hidden md:flex items-center justify-between mb-4">
                <div>
                    <h1 className="font-display text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
                        <MessageSquare className="w-6 h-6" />
                        Messagerie
                        {adminView && (
                            <Badge variant="destructive" className="ml-2">
                                <Shield className="w-3 h-3 mr-1" />
                                Admin
                            </Badge>
                        )}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                        {unreadCount.messages > 0 && (
                            <span className="text-primary font-medium">{unreadCount.messages} non lu(s)</span>
                        )}
                    </p>
                </div>
                <div className="flex gap-2">
                    {isAdmin && (
                        <Button 
                            variant={adminView ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                                setAdminView(!adminView);
                                if (!adminView) {
                                    fetchAllConversations();
                                    fetchAdminUsers();
                                }
                            }}
                            data-testid="admin-view-btn"
                        >
                            <Shield className="w-4 h-4 mr-2" />
                            {adminView ? 'Vue normale' : 'Vue Admin'}
                        </Button>
                    )}
                    {isAdmin && adminView && (
                        <Button 
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAdminUsersDialog(true)}
                            data-testid="admin-new-message-btn"
                        >
                            <User className="w-4 h-4 mr-2" />
                            Écrire à...
                        </Button>
                    )}
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowRunningLateDialog(true)}
                        data-testid="running-late-btn"
                    >
                        <Clock className="w-4 h-4 mr-2" />
                        Je suis en retard
                    </Button>
                    {/* Bouton Écrire à la Direction - visible pour tous sauf ADMIN */}
                    {user?.role !== 'ADMIN' && (
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowWriteToAdminDialog(true)}
                            className="border-purple-500 text-purple-600 hover:bg-purple-50"
                            data-testid="write-to-admin-btn"
                        >
                            <Shield className="w-4 h-4 mr-2" />
                            Écrire à la Direction
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowSettingsDialog(true)}
                        data-testid="notification-settings-btn"
                    >
                        <Settings className="w-4 h-4" />
                    </Button>
                </div>
            </div>
            
            {/* Mobile: Quick actions bar */}
            <div className="md:hidden flex gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4">
                <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowRunningLateDialog(true)}
                    className="shrink-0 h-9"
                >
                    <Clock className="w-4 h-4 mr-1.5" />
                    En retard
                </Button>
                {user?.role !== 'ADMIN' && (
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowWriteToAdminDialog(true)}
                        className="shrink-0 h-9 border-purple-500 text-purple-600"
                    >
                        <Shield className="w-4 h-4 mr-1.5" />
                        Direction
                    </Button>
                )}
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowNewPrivateDialog(true)}
                    className="shrink-0 h-9"
                >
                    <Plus className="w-4 h-4 mr-1.5" />
                    Nouveau
                </Button>
            </div>
            
            {/* Main content - Mobile: Full width list, Desktop: Two columns */}
            <div className="flex-1 flex flex-col md:flex-row gap-4 min-h-0">
                {/* Conversations list - Full width on mobile, fixed width on desktop */}
                <Card className={cn(
                    "flex flex-col",
                    "w-full md:w-80 md:shrink-0",
                    // On mobile, take full height when no conversation selected
                    !selectedGroup && "flex-1",
                    // On desktop always show, on mobile hide when conversation selected
                    selectedGroup && "hidden md:flex"
                )}>
                    <CardHeader className="py-3 px-4 border-b">
                        <div className="flex items-center justify-between">
                            <CardTitle className="text-sm font-medium">
                                {adminView ? 'Toutes les conversations' : 'Conversations'}
                            </CardTitle>
                            {!adminView && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 hidden md:flex"
                                    onClick={() => setShowNewPrivateDialog(true)}
                                    data-testid="new-private-chat-btn"
                                >
                                    <Plus className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                        <div className="relative mt-2">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-8 h-9 text-sm"
                            />
                        </div>
                    </CardHeader>
                    <ScrollArea className="flex-1">
                        <div className="p-2 space-y-1">
                            {/* Admin view - show all conversations */}
                            {adminView ? (
                                allConversations
                                    .filter(g => g.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                                g.member_names?.some(n => n?.toLowerCase().includes(searchQuery.toLowerCase())))
                                    .map((conv) => renderConversationItem(conv, true))
                            ) : (
                                /* Normal view */
                                groups
                                    .filter(g => g.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
                                                g.member_names?.some(n => n?.toLowerCase().includes(searchQuery.toLowerCase())))
                                    .map((g) => renderConversationItem(g, false))
                            )}
                            
                            {groups.length === 0 && !adminView && (
                                <div className="text-center py-8 text-muted-foreground">
                                    <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                                    <p className="text-sm">Aucune conversation</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                </Card>
                
                {/* Messages area - Hidden on mobile (uses fullscreen view), visible on desktop */}
                <Card className="hidden md:flex flex-1 flex-col">
                    {selectedGroup ? (
                        <>
                            {/* Group header */}
                            <CardHeader className="py-3 px-4 border-b flex-row items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg ${getGroupColor(selectedGroup.name)} flex items-center justify-center text-white`}>
                                        {getGroupIcon(selectedGroup)}
                                    </div>
                                    <div>
                                        <CardTitle className="text-base">{selectedGroup.name}</CardTitle>
                                        <p className="text-xs text-muted-foreground">
                                            {selectedGroup.type === 'PRIVATE' 
                                                ? 'Conversation privée' 
                                                : `${selectedGroup.member_count || 0} membres`}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {selectedGroup.is_muted ? (
                                        <BellOff className="w-4 h-4 text-muted-foreground" />
                                    ) : (
                                        <Bell className="w-4 h-4 text-muted-foreground" />
                                    )}
                                </div>
                            </CardHeader>
                            
                            {/* Messages */}
                            <ScrollArea className="flex-1 p-4">
                                <div className="space-y-4">
                                    {messages.map((message) => (
                                        <div
                                            key={message.id}
                                            className={`flex gap-3 ${message.sender_id === user?.id ? 'flex-row-reverse' : ''}`}
                                            data-testid={`message-${message.id}`}
                                        >
                                            <Avatar className="w-8 h-8 shrink-0">
                                                <AvatarFallback className={message.is_system ? 'bg-yellow-100 text-yellow-700' : ''}>
                                                    {message.is_system ? '⚙️' : getInitials(message.sender_name)}
                                                </AvatarFallback>
                                            </Avatar>
                                            <div className={`max-w-[70%] ${message.sender_id === user?.id ? 'items-end' : ''}`}>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-xs font-medium">
                                                        {message.is_system ? 'Système' : message.sender_name}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatMessageTime(message.created_at)}
                                                    </span>
                                                </div>
                                                <div className={`rounded-lg p-3 ${
                                                    message.is_deleted 
                                                        ? 'bg-muted/50 text-muted-foreground italic'
                                                        : message.is_system
                                                            ? 'bg-yellow-50 border border-yellow-200'
                                                            : message.sender_id === user?.id
                                                                ? 'bg-primary text-primary-foreground'
                                                                : 'bg-muted'
                                                }`}>
                                                    {message.is_deleted ? (
                                                        <span className="flex items-center gap-2">
                                                            <Trash2 className="w-4 h-4" />
                                                            Message supprimé
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                                                            
                                                            {/* Entity links */}
                                                            {message.entity_links?.length > 0 && (
                                                                <div className="mt-2 flex flex-wrap gap-2">
                                                                    {message.entity_links.map((link, i) => (
                                                                        <a
                                                                            key={i}
                                                                            href={link.url}
                                                                            className="inline-flex items-center gap-1 text-xs bg-background/50 rounded px-2 py-1 hover:bg-background"
                                                                        >
                                                                            <LinkIcon className="w-3 h-3" />
                                                                            {link.label}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Attachments */}
                                                            {message.attachments?.length > 0 && (
                                                                <div className="mt-2 space-y-1">
                                                                    {message.attachments.map((att, i) => (
                                                                        <a
                                                                            key={i}
                                                                            href={att.url}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="flex items-center gap-2 text-xs bg-background/50 rounded px-2 py-1 hover:bg-background"
                                                                        >
                                                                            {att.content_type?.startsWith('image/') ? (
                                                                                <ImageIcon className="w-4 h-4" />
                                                                            ) : (
                                                                                <FileText className="w-4 h-4" />
                                                                            )}
                                                                            {att.filename}
                                                                        </a>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                                
                                                {/* Message actions */}
                                                {!message.is_deleted && canDeleteMessages && (
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="ghost" size="icon" className="h-6 w-6 mt-1">
                                                                <MoreVertical className="w-3 h-3" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem
                                                                className="text-destructive"
                                                                onClick={() => {
                                                                    setSelectedMessage(message);
                                                                    setShowDeleteDialog(true);
                                                                }}
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Supprimer
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={messagesEndRef} />
                                </div>
                            </ScrollArea>
                            
                            {/* Message input */}
                            {selectedGroup.can_write?.includes('*') || selectedGroup.can_write?.includes(user?.role) ? (
                                <div className="p-4 border-t">
                                    <div className="flex gap-2">
                                        <Textarea
                                            placeholder="Écrire un message..."
                                            value={messageText}
                                            onChange={(e) => setMessageText(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendMessage();
                                                }
                                            }}
                                            className="min-h-[44px] max-h-32 resize-none"
                                            rows={1}
                                            data-testid="message-input"
                                        />
                                        <Button
                                            onClick={sendMessage}
                                            disabled={!messageText.trim() || sending}
                                            data-testid="send-message-btn"
                                        >
                                            <Send className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-4 border-t text-center text-sm text-muted-foreground">
                                    <Lock className="w-4 h-4 inline mr-2" />
                                    Vous ne pouvez pas écrire dans ce groupe
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                <p>Sélectionnez une conversation</p>
                            </div>
                        </div>
                    )}
                </Card>
            </div>
            
            {/* Delete Message Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <Trash2 className="w-5 h-5" />
                            Supprimer le message
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Cette action est irréversible. Un motif est obligatoire et sera enregistré dans le journal d'audit.
                        </p>
                        {selectedMessage && (
                            <div className="p-3 bg-muted rounded-lg text-sm">
                                <p className="font-medium">{selectedMessage.sender_name}</p>
                                <p className="text-muted-foreground mt-1">{selectedMessage.content?.slice(0, 100)}...</p>
                            </div>
                        )}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Motif de suppression *</label>
                            <Textarea
                                placeholder="Expliquez pourquoi ce message doit être supprimé..."
                                value={deleteReason}
                                onChange={(e) => setDeleteReason(e.target.value)}
                                className="min-h-[100px]"
                            />
                            {deleteReason.length > 0 && deleteReason.length < 5 && (
                                <p className="text-xs text-destructive">Minimum 5 caractères</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={deleteMessage}
                            disabled={deleteReason.length < 5}
                        >
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Running Late Dialog */}
            <Dialog open={showRunningLateDialog} onOpenChange={setShowRunningLateDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Signaler un retard
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Prévenez votre équipe que vous êtes en retard. Ce message sera envoyé au groupe de votre service.
                        </p>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Raison (optionnel)</label>
                            <Input
                                placeholder="Ex: Embouteillages, panne de réveil..."
                                value={runningLateData.reason}
                                onChange={(e) => setRunningLateData({...runningLateData, reason: e.target.value})}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Arrivée estimée dans</label>
                            <div className="flex gap-2">
                                {[5, 10, 15, 30, 45, 60].map((mins) => (
                                    <Button
                                        key={mins}
                                        variant={runningLateData.eta_minutes === mins ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => setRunningLateData({...runningLateData, eta_minutes: mins})}
                                    >
                                        {mins} min
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRunningLateDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={reportRunningLate}>
                            Envoyer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Dialog: Écrire à la Direction */}
            <Dialog open={showWriteToAdminDialog} onOpenChange={setShowWriteToAdminDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-purple-600" />
                            Écrire à la Direction
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <p className="text-sm text-purple-800">
                                <Lock className="w-4 h-4 inline mr-1" />
                                Ce message est <strong>confidentiel</strong> et ne sera lu que par l'administrateur.
                            </p>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Sujet (optionnel)</label>
                            <Input
                                placeholder="Ex: Demande de congé, Signalement..."
                                value={adminMessageData.subject}
                                onChange={(e) => setAdminMessageData({...adminMessageData, subject: e.target.value})}
                                data-testid="admin-message-subject"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Message *</label>
                            <Textarea
                                placeholder="Écrivez votre message ici..."
                                value={adminMessageData.content}
                                onChange={(e) => setAdminMessageData({...adminMessageData, content: e.target.value})}
                                rows={5}
                                data-testid="admin-message-content"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowWriteToAdminDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={sendMessageToAdmin}
                            disabled={sendingAdminMessage || !adminMessageData.content.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="send-admin-message-btn"
                        >
                            {sendingAdminMessage ? 'Envoi...' : 'Envoyer à la Direction'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* New Private Chat Dialog */}
            <Dialog open={showNewPrivateDialog} onOpenChange={setShowNewPrivateDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            Nouvelle conversation privée
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Sélectionnez un collègue pour démarrer une conversation privée.
                        </p>
                        <ScrollArea className="h-64">
                            <div className="space-y-1">
                                {users.map((u) => (
                                    <button
                                        key={u.id}
                                        onClick={() => startPrivateChat(u.id)}
                                        className="w-full p-3 rounded-lg text-left hover:bg-muted transition-colors flex items-center gap-3"
                                    >
                                        <Avatar className="w-8 h-8">
                                            <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-medium text-sm">{u.name}</p>
                                            <p className="text-xs text-muted-foreground">{u.role}</p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Notification Settings Dialog */}
            <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Bell className="w-5 h-5" />
                            Paramètres de notification
                        </DialogTitle>
                    </DialogHeader>
                    {notificationSettings && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-sm">Messages privés</p>
                                    <p className="text-xs text-muted-foreground">Notifications pour les messages privés</p>
                                </div>
                                <Button
                                    variant={notificationSettings.push_private_messages ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => updateNotificationSettings({
                                        push_private_messages: !notificationSettings.push_private_messages
                                    })}
                                >
                                    {notificationSettings.push_private_messages ? 'Activé' : 'Désactivé'}
                                </Button>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-sm">Mentions</p>
                                    <p className="text-xs text-muted-foreground">Quand quelqu'un vous mentionne</p>
                                </div>
                                <Button
                                    variant={notificationSettings.push_mentions ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => updateNotificationSettings({
                                        push_mentions: !notificationSettings.push_mentions
                                    })}
                                >
                                    {notificationSettings.push_mentions ? 'Activé' : 'Désactivé'}
                                </Button>
                            </div>
                            
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-sm">Annonces</p>
                                    <p className="text-xs text-muted-foreground">Annonces officielles de la Direction</p>
                                </div>
                                <Button
                                    variant={notificationSettings.push_announcements ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => updateNotificationSettings({
                                        push_announcements: !notificationSettings.push_announcements
                                    })}
                                >
                                    {notificationSettings.push_announcements ? 'Activé' : 'Désactivé'}
                                </Button>
                            </div>
                            
                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <p className="font-medium text-sm">Mode silencieux</p>
                                        <p className="text-xs text-muted-foreground">Pas de notifications pendant ces heures</p>
                                    </div>
                                    <Button
                                        variant={notificationSettings.quiet_hours_enabled ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => updateNotificationSettings({
                                            quiet_hours_enabled: !notificationSettings.quiet_hours_enabled
                                        })}
                                    >
                                        {notificationSettings.quiet_hours_enabled ? 'Activé' : 'Désactivé'}
                                    </Button>
                                </div>
                                {notificationSettings.quiet_hours_enabled && (
                                    <p className="text-sm text-muted-foreground">
                                        De {notificationSettings.quiet_hours_start} à {notificationSettings.quiet_hours_end}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* IBAKA Alert Confirmation Dialog */}
            <Dialog open={showIbakaWarning} onOpenChange={setShowIbakaWarning}>
                <DialogContent className="border-red-500 border-2">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle className="w-6 h-6" />
                            ALERTE URGENCE IBAKA
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                            <p className="text-sm text-red-800 font-medium mb-2">
                                Le mot IBAKA est réservé aux urgences graves :
                            </p>
                            <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                                <li>Violence ou agression</li>
                                <li>Vol ou cambriolage</li>
                                <li>Urgence médicale</li>
                                <li>Incendie ou danger immédiat</li>
                            </ul>
                        </div>
                        
                        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                            <p className="text-sm text-yellow-800">
                                <strong>⚠️ Attention :</strong> Une alerte IBAKA sera envoyée à <strong>TOUS</strong> les utilisateurs 
                                avec une notification prioritaire qui contourne le mode silencieux.
                            </p>
                        </div>
                        
                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="ibaka-confirm"
                                checked={ibakaConfirmed}
                                onCheckedChange={(checked) => setIbakaConfirmed(checked)}
                                className="border-red-500 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                            />
                            <label htmlFor="ibaka-confirm" className="text-sm cursor-pointer">
                                Je confirme qu'il s'agit d'une <strong>urgence réelle</strong>. 
                                J'ai conscience que toute utilisation abusive peut entraîner une sanction.
                            </label>
                        </div>
                    </div>
                    <DialogFooter className="flex gap-2">
                        <Button 
                            variant="outline" 
                            onClick={() => {
                                setShowIbakaWarning(false);
                                setIbakaConfirmed(false);
                            }}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={sendIbakaAlert}
                            disabled={!ibakaConfirmed || sendingIbaka}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            {sendingIbaka ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                    Envoi...
                                </>
                            ) : (
                                <>
                                    <AlertTriangle className="w-4 h-4 mr-2" />
                                    ENVOYER L'ALERTE IBAKA
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            
            {/* Admin: Write to User Dialog */}
            <Dialog open={showAdminUsersDialog} onOpenChange={setShowAdminUsersDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Shield className="w-5 h-5 text-primary" />
                            Écrire à un utilisateur
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            En tant qu'administrateur, vous pouvez envoyer un message direct à n'importe quel utilisateur.
                        </p>
                        <ScrollArea className="h-80">
                            <div className="space-y-2">
                                {adminUsers.map((u) => (
                                    <button
                                        key={u.id}
                                        onClick={async () => {
                                            // Start private conversation
                                            try {
                                                const res = await api().post(`/messaging/private/${u.id}`);
                                                setSelectedGroup(res.data);
                                                setShowAdminUsersDialog(false);
                                                fetchAdminMessages(res.data.id);
                                                setAdminView(true);
                                            } catch (error) {
                                                toast.error('Erreur');
                                            }
                                        }}
                                        className="w-full p-3 rounded-lg text-left hover:bg-muted transition-colors flex items-center justify-between"
                                    >
                                        <div className="flex items-center gap-3">
                                            <Avatar className="w-10 h-10">
                                                <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                                            </Avatar>
                                            <div>
                                                <p className="font-medium text-sm">{u.name}</p>
                                                <p className="text-xs text-muted-foreground">{u.email}</p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <Badge variant="outline" className="text-xs">
                                                {u.role}
                                            </Badge>
                                            {u.has_conversation && (
                                                <p className="text-[10px] text-muted-foreground mt-1">
                                                    Conversation existante
                                                </p>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </ScrollArea>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MessagingPage;
